import { useState, useRef, useEffect, useId } from 'react';
import { SearchModal } from './SearchModal';
import { getCareerYear } from '../../../utils/date';

const YOSHIMOTO_ORANGE = '#D75428';
const PANEL_BLUE = '#2857D7';
const CORNER_RADIUS = 14;

// 多角形の各頂点をQベジェで丸めたSVG pathを生成
const buildRoundedPolygonPath = (points, r) => {
  const n = points.length;
  let d = '';
  for (let i = 0; i < n; i++) {
    const curr = points[i];
    const prev = points[(i - 1 + n) % n];
    const next = points[(i + 1) % n];

    const v1x = prev[0] - curr[0];
    const v1y = prev[1] - curr[1];
    const len1 = Math.hypot(v1x, v1y) || 1;
    const r1 = Math.min(r, len1 / 2);
    const inX = curr[0] + (v1x / len1) * r1;
    const inY = curr[1] + (v1y / len1) * r1;

    const v2x = next[0] - curr[0];
    const v2y = next[1] - curr[1];
    const len2 = Math.hypot(v2x, v2y) || 1;
    const r2 = Math.min(r, len2 / 2);
    const outX = curr[0] + (v2x / len2) * r2;
    const outY = curr[1] + (v2y / len2) * r2;

    d += i === 0 ? `M ${inX},${inY} ` : `L ${inX},${inY} `;
    d += `Q ${curr[0]},${curr[1]} ${outX},${outY} `;
  }
  return d + 'Z';
};

export const SelectionCard = ({ selectedTarget, onSelect, label, highlight }) => {
  const COLOR = label === 'B' ? PANEL_BLUE : YOSHIMOTO_ORANGE;
  const [modalOpen, setModalOpen] = useState(false);
  const wrapperRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const reactId = useId();
  const clipId = `trap-clip-${reactId.replace(/:/g, '')}`;

  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    const update = () => setSize({ w: el.offsetWidth, h: el.offsetHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleClear = (e) => {
    e.stopPropagation();
    onSelect(null);
  };

  const careerYear = selectedTarget ? getCareerYear(selectedTarget.startYear) : null;
  const isGroup = selectedTarget?.type === 'group';

  // 台形の頂点（時計回り）
  const points = label === 'A'
    ? [[0, 0], [size.w, 0], [size.w, size.h], [0, size.h * 0.8]]
    : [[0, 0], [size.w, size.h * 0.2], [size.w, size.h], [0, size.h]];
  const pathD = size.w > 0 && size.h > 0 ? buildRoundedPolygonPath(points, CORNER_RADIUS) : '';

  const header = (
    <div
      style={{
        background: COLOR,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexDirection: label === 'B' ? 'row-reverse' : 'row',
      }}
    >
      <span
        style={{
          flex: 1,
          fontSize: selectedTarget ? 17 : 15,
          fontWeight: selectedTarget ? 'bold' : 'normal',
          color: selectedTarget ? '#fff' : 'rgba(255,255,255,0.6)',
          textAlign: label === 'B' ? 'right' : 'left',
        }}
      >
        {selectedTarget ? selectedTarget.name : '芸人またはグループを選択'}
      </span>

      {selectedTarget ? (
        <button
          onClick={handleClear}
          style={{
            background: 'rgba(255,255,255,0.25)',
            border: 'none',
            borderRadius: '50%',
            width: 26,
            height: 26,
            cursor: 'pointer',
            color: '#fff',
            fontSize: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          ✕
        </button>
      ) : (
        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 20, fontWeight: 'bold' }}>›</span>
      )}
    </div>
  );

  const content = (
    <div
      style={{
        position: 'relative',
        background: '#fff',
        padding: '14px 18px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: label === 'A' ? 'flex-start' : 'flex-end',
        minHeight: 140,
      }}
    >
      {selectedTarget ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: label === 'B' ? 'row-reverse' : 'row',
            gap: 16,
          }}
        >
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 13, color: '#888', margin: '0 0 6px' }}>
              {isGroup ? 'グループ' : `芸人 · ${selectedTarget.agency}`}
            </p>
            <p style={{ fontSize: 15, color: '#555', margin: 0, lineHeight: 1.6, fontWeight: 'bold' }}>
              {isGroup
                ? `結成: ${selectedTarget.startYear}年（${selectedTarget.members.map((m) => m.name).join('・')}）`
                : `デビュー: ${selectedTarget.startYear}年（${selectedTarget.groups.map((g) => g.name).join('・')}）`}
            </p>
          </div>
          <div
            style={{
              textAlign: 'center',
              flexShrink: 0,
              background: label === 'B' ? '#f2f4ff' : '#fff5f2',
              borderRadius: 12,
              padding: '6px 12px',
              border: `2px solid ${COLOR}`,
            }}
          >
            <span
              style={{
                fontSize: 36,
                fontWeight: 'bold',
                color: COLOR,
                lineHeight: 1,
                display: 'block',
              }}
            >
              {careerYear}
            </span>
            <span style={{ fontSize: 12, color: COLOR, fontWeight: 'bold' }}>年目</span>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {pathD && (
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
              <path d={pathD} />
            </clipPath>
          </defs>
        </svg>
      )}

      <div
        ref={wrapperRef}
        onClick={() => setModalOpen(true)}
        style={{
          clipPath: pathD ? `url(#${clipId})` : undefined,
          cursor: 'pointer',
          boxShadow: highlight
            ? label === 'B'
              ? '0 4px 16px rgba(40,87,215,0.35)'
              : '0 4px 16px rgba(229,81,38,0.35)'
            : '0 2px 8px rgba(0,0,0,0.08)',
          transform: highlight ? 'scale(1.01)' : 'scale(1)',
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
      >
        {label === 'B' ? (
          <>
            {content}
            {header}
          </>
        ) : (
          <>
            {header}
            {content}
          </>
        )}
      </div>

      {modalOpen && (
        <SearchModal
          onSelect={onSelect}
          onClose={() => setModalOpen(false)}
          label={label}
        />
      )}
    </>
  );
};
